import { useNavigate } from 'react-router-dom'

import {
  Avatar,
  Box,
  Button,
  TagColor as Color,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import { ProjectionKind, useContributorsDataTable } from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { UpsellContributorsMessage } from '../../upsells/UpsellContributorsMessage'
import NoContributorsMessage from './NoContributorsMessage'

const ContributorsDataTable = ({ hasContributorsFeature }: { hasContributorsFeature: boolean }) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const source = useContributorsDataTable(
    [
      {
        kind: ProjectionKind.Decorative,
        identifier: 'avatar',
        width: 80,
        render: ({ employee_fullName, employee_access }) => {
          return (
            employee_fullName &&
            employee_access && (
              <Avatar
                size="s40"
                url={buildUrl(employee_access.avatar)}
                label={employee_fullName}
                key={employee_access.id}
              />
            )
          )
        },
      },
      {
        kind: ProjectionKind.Custom,
        identifier: 'fullName',
        template: 'fullName',
        title: i18n.t('project_management.table.contributors_list.headers.full_name'),
        sortBy: 'fullName',
        render: ({ employee_fullName }) => {
          return (
            <Box minWidth="s200">
              <Text.Normal overflowWrap>{employee_fullName}</Text.Normal>
            </Box>
          )
        },
      },
      {
        kind: ProjectionKind.Custom,
        identifier: '{{employee_referenceContract_jobTitle}}',
        template: '{{employee_referenceContract_jobTitle}}',
        title: i18n.t('project_management.table.contributors_list.headers.job_title'),
        render: ({ employee_referenceContract_jobTitle }) => {
          return (
            <Box minWidth="s200">
              <Text.Normal overflowWrap>{employee_referenceContract_jobTitle || '-'}</Text.Normal>
            </Box>
          )
        },
      },
      {
        kind: ProjectionKind.Custom,
        identifier: '{{projectWorkers}}',
        template: '{{projectWorkers}}',
        title: i18n.t('project_management.table.contributors_list.headers.projects_assigned'),
        justifyContent: 'center',
        render: ({ projectWorkersConnection_totalCount }) => {
          return (
            <Box justifyContent="center" alignItems="center">
              <Tag inline type={Color.grey}>
                <Text.Normal>{projectWorkersConnection_totalCount}</Text.Normal>
              </Tag>
            </Box>
          )
        },
      },
      {
        kind: ProjectionKind.Decorative,
        identifier: 'actions',
        width: 80,
        render: ({ employee_id }) => {
          return (
            employee_id && (
              <Button
                iconName="arrow-right"
                type="secondary"
                onClick={() =>
                  navigate(
                    navigation.projects.contributors.contributorId.fullPath({
                      employeeId: employee_id,
                    })
                  )
                }
              />
            )
          )
        },
      },
    ],
    { variables: { employeeName: debouncedSearch } }
  )

  return (
    <DataTable.Provider
      source={source}
      pagination={{
        kind: 'offset',
        itemsPerPage: 25,
        infiniteScroll: false,
      }}
      sort={{
        path: 'fullName',
      }}
    >
      <Box gap="s16">
        <DataTable.HeadBar
          disableExport
          search={{
            placeholder: i18n.t('common.search_by'),
            value: search,
            onChange: onSearch,
          }}
        />
        <DataTable.Table
          autoWidth
          emptyState={
            !hasContributorsFeature ? <UpsellContributorsMessage /> : <NoContributorsMessage />
          }
        />
      </Box>
    </DataTable.Provider>
  )
}

export default ContributorsDataTable
